import React, { useState, useEffect } from 'react';

import { Link } from 'react-router-dom';
import { Button, Grid, Container, Typography } from '@mui/material';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import RestUtilities from '../services/RestUtilities';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  artistState,
  galleryState,
  loadingState,
  toastState,
} from '../state/RecoilState';
import { ArtistMedia, Upload } from '../@types/api';
import MustPickArtist from '../components/Common/MustPickArtist';
import ModalFileManager from '../components/Common/ModalFileManager';
import { uploadType } from '../Constants';
import ArtistMediaCard from '../components/ArtistMediaCard';

const sortImages = (a: ArtistMedia, b: ArtistMedia) => {
  if (a.isLive < b.isLive) {
    return 1;
  } else if (a.isLive > b.isLive) {
    return -1;
  } else if (a.orderIndex > b.orderIndex) {
    return 1;
  } else if (a.orderIndex < b.orderIndex) {
    return -1;
  }
  return 0;
};

const Images = () => {
  const artist = useRecoilValue(artistState);
  const setToast = useSetRecoilState(toastState);
  const setLoading = useSetRecoilState(loadingState);
  const [gallery, setGallery] = useRecoilState<ArtistMedia[] | undefined>(
    galleryState
  );
  const [selectedId, setSelectedId] = useState<number | undefined>(undefined);
  const [show, setShow] = useState<boolean>(false);
  const [fileType, setFileType] = useState<number>(uploadType.video);

  useEffect(() => {
    const fetchGallery = async (artistId: any) => {
      setLoading(true);
      const url = `api/ArtistMedia/Images/${artistId}`;
      const response = await RestUtilities.get(url);
      setLoading(false);
      if (response.ok) {
        setGallery(response.content);
      }
    };
    if (artist && !gallery) {
      fetchGallery(artist.artistId);
    }
  }, [artist, gallery, setLoading, setGallery]);

  const onRemoveMedia = async (artistMedia: ArtistMedia) => {
    const url = `api/ArtistMedia/${artistMedia.artistMediaId}/${artist?.artistId}`;
    var response = await RestUtilities.delete(url);
    if (response.ok && gallery) {
      const newMedia = gallery.filter(
        (x) => x.artistMediaId !== artistMedia.artistMediaId
      );
      setGallery(newMedia);
      setToast('Image removed');
    }
  };

  const onSaveMedia = (updatedMedia: ArtistMedia) => {
    RestUtilities.put(
      `api/ArtistMedia/${updatedMedia.artistMediaId}/${artist?.artistId}`,
      updatedMedia
    );
    setToast('Image updated');
    if (!gallery) return;
    const newMedia = [
      ...gallery.filter((x) => x.artistMediaId !== updatedMedia.artistMediaId),
      updatedMedia,
    ];
    setGallery(newMedia);
  };

  const renderImages = () => {
    if (!gallery) return null;
    const sortedImages = gallery.toSorted(sortImages);
    return sortedImages.map((x: ArtistMedia) => {
      return (
        <Grid
          key={x.artistMediaId}
          item
          lg={3}
          md={4}
          sm={6}
          onClick={(e) => onItemSelect(e, x)}
        >
          <ArtistMediaCard
            media={x}
            onSaveMedia={onSaveMedia}
            onRemoveMedia={onRemoveMedia}
            isSelected={x.artistMediaId === selectedId}
          />
        </Grid>
      );
    });
  };

  const onItemSelect = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    item: ArtistMedia
  ) => {
    if (selectedId !== item.artistMediaId) {
      e.preventDefault();
      setSelectedId(item.artistMediaId);
    }
  };

  const handleUploadSelect = async (item: Upload) => {
    if (!gallery) {
      setShow(false);
      return;
    } else {
      if (!artist) return;
      const artistMedia: ArtistMedia = {
        artistId: artist.artistId,
        mediaTypeId: 1,
        uploadId: item.uploadId,
        isLive: false,
        orderIndex: 99,
      };
      setLoading(true);
      const response = await RestUtilities.post(
        `api/ArtistMedia/${artist.artistId}`,
        artistMedia
      );
      if (response.ok) {
        setToast('Image added');
        const newGallery = [...gallery, response.content];
        setGallery(newGallery);
      }
      setShow(false);
      setLoading(false);
    }
  };

  if (!artist) {
    return <MustPickArtist />;
  }

  return (
    <Container maxWidth='lg'>
      <Typography variant='h4' gutterBottom>
        {artist.artistName} Gallery Images
      </Typography>
      <Typography variant='body2'>
        This is the current images appearing on your band&#39;s website. You can
        reorder and hide images. You can edit image filenames and alt texts in
        the <Link to={'/files'}>File Editor</Link>.
      </Typography>

      {gallery && (
        <>
          <Grid container spacing={2} className='images-row'>
            <Grid item xs={12}>
              <Button
                variant='contained'
                color='primary'
                endIcon={<AddAPhotoIcon />}
                size='small'
                onClick={() => {
                  setFileType(uploadType.image);
                  setShow(true);
                }}
                sx={{ mt: 2 }}
              >
                Add new image
              </Button>
            </Grid>
            {renderImages()}
          </Grid>
        </>
      )}
      <ModalFileManager
        show={show}
        handleClose={() => setShow(false)}
        fileType={fileType}
        handleUploadSelect={handleUploadSelect}
      />
    </Container>
  );
};

export default Images;
